import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { DonationBadge } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/JointBillTable/components/DonationBill/components/DonationBadge/DonationBadge";
import { NonProfitProviderRow } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/JointBillTable/components/NonProfitProviders/components/NonProfitProviderRow/NonProfitProviderRow";
import { ProviderBillNameCell } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/JointBillTable/components/ProviderBill/components/ProviderBillNameCell/ProviderBillNameCell";
import { JointBillTableRowLayout } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/layouts/JointBillTableRowLayout/JointBillTableRowLayout";
import { JointBillContext } from "@features/JointBillList/utils";
import { IconButton } from "@components/IconButton/IconButton";
import { CrossIcon } from "@components/icons";
import { useHideNonProfitProvidersMutation } from "@services/nonProfits/mutations";
import { type IOverviewJointBillService } from "@services/overview/types";
import { isNullOrUndefined } from "@utils/common";
import { COUNTRY_CODE } from "@/constants";
import { CountryCode } from "@/i18n";
import { SingleNonProfitProvider } from "../SingleNonProfitProvider/SingleNonProfitProvider";

interface IDonationBillProps {
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"];
  periodId: number;
}

export const NonProfitProviders: React.FC<IDonationBillProps> = ({
  jointBillServiceId,
  periodId,
}) => {
  const { t } = useTranslation();
  const { nonProfitProviders } = useContext(JointBillContext);

  const { mutateAsync: hideNonProfitProviders } =
    useHideNonProfitProvidersMutation(jointBillServiceId);

  if (
    COUNTRY_CODE === CountryCode.LT &&
    !isNullOrUndefined(nonProfitProviders?.[0])
  ) {
    return (
      <SingleNonProfitProvider
        nonProfitProvider={nonProfitProviders[0]}
        jointBillServiceId={jointBillServiceId}
        periodId={periodId}
      />
    );
  }

  if (nonProfitProviders?.length) {
    return (
      <JointBillTableRowLayout
        fullWidthBorderTop={true}
        className="bg-blue-light"
        nameCell={
          <ProviderBillNameCell
            name={t("nonProfitProviders.title")}
            providerBadge={<DonationBadge />}
          />
        }
        statusCell={
          <span className="whitespace-nowrap">
            {t("nonProfitProviders.description")}
          </span>
        }
        totalCell={
          <div className="flex h-full items-center">
            <IconButton
              icon={CrossIcon}
              onClick={() => hideNonProfitProviders()}
              aria-label={t("action.close")}
            />
          </div>
        }
        detailsOpenButtonTitle={t(
          "nonProfitProviders.actions.detailsOpenButton",
        )}
        detailsCloseButtonTitle={t(
          "nonProfitProviders.actions.detailsCloseButton",
        )}
        billDetails={
          <div className="flex flex-col divide-y divide-grey-light">
            {nonProfitProviders.map((provider) => (
              <NonProfitProviderRow
                key={provider.serviceProviderId}
                jointBillServiceId={jointBillServiceId}
                provider={provider}
                periodId={periodId}
              />
            ))}
          </div>
        }
      />
    );
  }

  return null;
};
